import {
  productFamilyIdFormat,
  productIdFormat,
  supplierIdFormat,
} from "@redotech/redo-model/ids";
import { AdvancedFilterDataSchema } from "@redotech/redo-model/views/views";
import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod/zod-enum";
import * as z from "zod/v4";

const inventoryProductSchema_ = z.object({
  id: productIdFormat.schema,
  productFamilyId: productFamilyIdFormat.schema,
  image: z.string().nullish(),
  title: z.string(),
  sku: z.string().nullish(),
  upc: z.string().nullish(),
  vendor: z.string().nullish(),
  tags: z.array(z.string()),
  totalQuantity: z.number(),
  totalBuildable: z.number(),
  totalAvailable: z.number(),
  totalCommitted: z.number(),
  totalReserved: z.number(),
  cost: z.number().nullish(),
  price: z.number().nullish(),
  currency: z.string().nullish(),
  accountId: z.string(),
  accountName: z.string(),
  incomingShipmentCount: z.number(),
  incomingUnits: z.number(),
  inventoryValue: z.number().nullish(),
  inventoryValueCurrency: z.string().nullish(),
  retailValue: z.number().nullish(),
  retailValueCurrency: z.string().nullish(),

  // Forecasting

  stockoutDate: zExt.plainDate().nullish(),
  stockoutDateCapped: z.boolean().optional(),
  daysOnHandDate: zExt.plainDate().nullish(),
  daysOnHandDateCapped: z.boolean().optional(),
  reorderThreshold: z.number().nullish(),
  reorderThresholdDate: zExt.plainDate().nullish(),
  moq: z.number().nullish(),
  leadTime: z.number().nullish(),
  buffer: z.number().nullish(),
  coverageDays: z.number().nullish(),
  unitsPerBox: z.number().int().positive().nullish(),
  forecastingEnabled: z.boolean(),
  suppliers: z.array(
    z.object({ id: supplierIdFormat.schema, name: z.string() }),
  ),
  // Reorder calculation fields
  reorderPoint: z.number().nullish(),
  daysUntilReorder: z.number().nullish(),
  unitsToOrder: z.number().nullish(),
  missingReorderParams: z
    .array(createEnum(["leadTime", "coverageDays", "forecast", "available"]))
    .optional(),
  // Purchase order info
  purchaseOrderCount: z.number().int(),
  // Cost (from Shopify unit cost, stored on product)
  costFractional: z.number().nullish(),
  // Historical sales (past X days)
  sold7Days: z.number().nullish(),
  sold14Days: z.number().nullish(),
  sold21Days: z.number().nullish(),
  sold30Days: z.number().nullish(),
  sold45Days: z.number().nullish(),
  sold60Days: z.number().nullish(),
  sold90Days: z.number().nullish(),
  sold120Days: z.number().nullish(),
  sold180Days: z.number().nullish(),
  sold365Days: z.number().nullish(),
  // Forecasted sales (next X days)
  forecast7Days: z.number().nullish(),
  forecast14Days: z.number().nullish(),
  forecast21Days: z.number().nullish(),
  forecast30Days: z.number().nullish(),
  forecast45Days: z.number().nullish(),
  forecast60Days: z.number().nullish(),
  forecast90Days: z.number().nullish(),
  forecast120Days: z.number().nullish(),
  forecast180Days: z.number().nullish(),
  forecast365Days: z.number().nullish(),
  // Run rates (units/day based on historical sales)
  runRate30Days: z.number().nullish(),
  runRate60Days: z.number().nullish(),
  runRate90Days: z.number().nullish(),
  runRate180Days: z.number().nullish(),
  runRate365Days: z.number().nullish(),
});

export const InventoryProductSchema = inventoryProductSchema_;
export type InventoryProduct = z.infer<typeof inventoryProductSchema_>;

export const getInventoryProducts = {
  name: "getInventoryProducts" as const,
  input: z.object({
    page: z.number().int().min(0).default(0),
    pageSize: z.number().int(),
    search: z.string().optional(),
    sortBy: createEnum([
      "title",
      "sku",
      "vendor",
      "cost",
      "totalAvailable",
      "totalQuantity",
      "totalBuildable",
      "totalCommitted",
      "totalReserved",
      "inventoryValue",
      "retailValue",

      // Forecasting
      "daysOfStockRemaining",
      "moq",
      "leadTime",
      "sold7Days",
      "sold14Days",
      "sold21Days",
      "sold30Days",
      "sold45Days",
      "sold60Days",
      "sold90Days",
      "sold120Days",
      "sold180Days",
      "sold365Days",
      "forecast7Days",
      "forecast14Days",
      "forecast21Days",
      "forecast30Days",
      "forecast45Days",
      "forecast60Days",
      "forecast90Days",
      "forecast120Days",
      "forecast180Days",
      "forecast365Days",
      "daysUntilReorder",
      "unitsToOrder",
    ]).optional(),
    sortDirection: createEnum(["asc", "desc"]).optional(),
    advancedFiltersData: z.array(AdvancedFilterDataSchema).optional(),
    // Forecasting
    today: zExt.plainDate().optional(),
  }),
  output: z.object({
    products: z.array(InventoryProductSchema),
    hasMultipleMerchants: z.boolean(),
  }),
};
